import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { AVDELNING_FARGER } from '../../lib/constants'
import { blockeraAuthLyssnare, avblockeraAuthLyssnare } from '../../lib/authGuard'
import { useAuth } from '../../hooks/useAuth'
import { BekraftaModal } from '../../components/BekraftaModal'

const AVDELNINGAR = ['RN', 'LN MMÖ', 'LN NKP', 'DISP']

export function MedarbetarePage() {
  const { refreshMedarbetare } = useAuth()
  const [medarbetare, setMedarbetare] = useState([])
  const [visaModal, setVisaModal] = useState(false)
  const [redigerar, setRedigerar] = useState(null)
  const [loading, setLoading] = useState(true)
  const [avslutaDialog, setAvslutaDialog] = useState(null)
  const [valdAvdelning, setValdAvdelning] = useState('Alla')
  const [sparar, setSparar] = useState(false)
  const [genereratLosenord, setGenereratLosenord] = useState('')
  const [kopierat, setKopierat] = useState(false)

  function slumpaLosenord() {
    const tecken = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
    let nytt = ''
    for (let i = 0; i < 9; i++) nytt += tecken[Math.floor(Math.random() * tecken.length)]
    setGenereratLosenord(nytt)
    setKopierat(false)
  }

  function kopieraLosenord() {
    navigator.clipboard.writeText(genereratLosenord)
    setKopierat(true)
    setTimeout(() => setKopierat(false), 2000)
  }
  const [form, setForm] = useState({
    fornamn: '', efternamn: '', kid: '', email: '', anstallningsnummer: '',
    avdelning: 'RN', schema_typ: '34.5h', ar_admin: false
  })

  useEffect(() => { hamtaMedarbetare() }, [])

  async function hamtaMedarbetare() {
    const { data } = await supabase
      .from('medarbetare')
      .select('*')
      .order('fornamn')
    setMedarbetare(data || [])
    setLoading(false)
  }

  function oppnaLaggTill() {
    setRedigerar(null)
    setForm({ fornamn: '', efternamn: '', kid: '', email: '', anstallningsnummer: '', avdelning: 'RN', schema_typ: '34.5h', ar_admin: false })
    setGenereratLosenord('')
    setVisaModal(true)
  }

  function oppnaRedigera(med) {
    setRedigerar(med)
    setForm({ fornamn: med.fornamn, efternamn: med.efternamn, kid: med.kid, email: med.email, anstallningsnummer: med.anstallningsnummer || '', avdelning: med.avdelning, schema_typ: med.schema_typ, ar_admin: med.ar_admin })
    setVisaModal(true)
  }

  function avslutaAnstallning(med) {
    setAvslutaDialog(med)
  }

  async function bekraftaAvsluta() {
    const med = avslutaDialog
    setAvslutaDialog(null)

    const anonymId = med.id.substring(0, 8)
    await supabase.from('medarbetare').update({
      ar_aktiv: false,
      fornamn: 'Anonym',
      efternamn: anonymId,
      email: `anonym.${anonymId}@borttagen.local`,
      kid: 'BORTTAGEN'
    }).eq('id', med.id)

    hamtaMedarbetare()
  }

  // Räknar om X-dagen (balansdagen) för en medarbetare i alla perioder de har
  // schema i, utifrån deras (eventuellt nya) schematyp. Behövs eftersom
  // X-dagen annars bara räknas ut en gång, vid själva Excel-importen.
  async function omraknaXforMedarbetare(medId, avdelning, nyttSchemaTyp) {
    const { data: perioderData } = await supabase.from('perioder').select('*').eq('avdelning', avdelning)
    for (const period of (perioderData || [])) {
      const veckor = period.antal_veckor
      const kravTimmar = (veckor === 8 || nyttSchemaTyp === '40h') ? veckor * 40 : veckor * 34.5

      const { data: periodPass } = await supabase
        .from('pass')
        .select('id, arbetstimmar, passtyp_namn')
        .eq('medarbetare_id', medId)
        .gte('datum', period.startdatum)
        .lte('datum', period.slutdatum)
      if (!periodPass || periodPass.length === 0) continue

      const arXNamn = ['X', 'SX', 'LX', 'SJX']
      const ovrigaTimmar = periodPass
        .filter(p => !arXNamn.includes((p.passtyp_namn || '').toUpperCase()))
        .reduce((sum, p) => sum + (p.arbetstimmar || 0), 0)
      const xTimmar = Math.round((kravTimmar - ovrigaTimmar) * 10) / 10

      const xIds = periodPass
        .filter(p => arXNamn.includes((p.passtyp_namn || '').toUpperCase()))
        .map(p => p.id)
      if (xIds.length > 0) {
        await supabase.from('pass').update({ arbetstimmar: xTimmar }).in('id', xIds)
      }
    }
  }

  async function handleSpara() {
    if (!redigerar && !genereratLosenord) {
      alert('Slumpa fram ett lösenord först.')
      return
    }
    setSparar(true)
    try {
      if (redigerar) {
        // Uppdatera befintlig
        const schemaTypAndrad = redigerar.schema_typ !== form.schema_typ
        await supabase.from('medarbetare').update(form).eq('id', redigerar.id)
        if (schemaTypAndrad) {
          await omraknaXforMedarbetare(redigerar.id, form.avdelning, form.schema_typ)
        }
      } else {
        // VIKTIGT: signUp() loggar automatiskt in webbläsaren som den NYA användaren.
        // Sätt på spärren FÖRST så useAuth ignorerar den tillfälliga inloggningen helt.
        blockeraAuthLyssnare()

        // Spara adminens egen session innan vi kör signUp, så vi kan återställa den direkt efteråt.
        const { data: { session: adminSession } } = await supabase.auth.getSession()

        try {
          // Skapa ny användare med det slumpade lösenordet (samma som skickas i mailet)
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: form.email,
            password: genereratLosenord,
            options: {
              data: { fornamn: form.fornamn, efternamn: form.efternamn }
            }
          })
          if (authError) throw authError
          if (!authData.user) throw new Error('Kunde inte skapa användare')

          // VIKTIGT: återställ admins session INNAN vi sparar medarbetarraden.
          // signUp() ovan loggade annars in webbläsaren som den NYA (ännu
          // tomma) användaren, och databasens behörighetsregler skulle då
          // inte känna igen sparningen som gjord av en admin - insättningen
          // skulle nekas tyst, utan felmeddelande.
          if (adminSession) {
            await supabase.auth.setSession({
              access_token: adminSession.access_token,
              refresh_token: adminSession.refresh_token
            })
          }

          const { error: insertError } = await supabase.from('medarbetare').insert({
            id: authData.user.id,
            maste_byta_losenord: true,
            ...form
          })
          if (insertError) throw insertError
        } finally {
          // Stäng av spärren och hämta admins egen data på riktigt, en gång, rent
          avblockeraAuthLyssnare()
          await refreshMedarbetare()
        }

        // Öppna ett förifyllt mail-utkast i admins egen mailapp (Outlook/Gmail) –
        // skickas manuellt av admin, kringgår problemet med blockerad automatisk e-post
        const appLank = 'https://eonschema.se'
        const amne = encodeURIComponent('Välkommen till E.ON Schema – dina inloggningsuppgifter')
        const brodtext = encodeURIComponent(
`Hej ${form.fornamn}!

Här är dina inloggningsuppgifter till E.ON Schema. Appen används via mobilen – öppna länken nedan i mobilens webbläsare.

Länk: ${appLank}
Lösenord: ${genereratLosenord}

Du behöver välja ett eget lösenord första gången du loggar in.

SÅ HÄR SPARAR DU APPEN PÅ HEMSKÄRMEN:

Android (Chrome):
1. Öppna länken ovan
2. Tryck på de tre punkterna uppe till höger
3. Välj "Lägg till på startskärmen"

iPhone (Safari):
1. Öppna länken ovan
2. Tryck på Dela-ikonen (fyrkant med pil uppåt) längst ner
3. Välj "Lägg till på hemskärmen"

Hälsningar,
Admin`)
        window.location.href = `mailto:${form.email}?subject=${amne}&body=${brodtext}`
      }
      setVisaModal(false)
      hamtaMedarbetare()
    } catch (err) {
      alert('Fel: ' + err.message)
    } finally {
      setSparar(false)
    }
  }

  if (loading) return <div className="spinner" />

  const filtrerade = medarbetare.filter(m => 
    m.ar_aktiv !== false && 
    (valdAvdelning === 'Alla' || m.avdelning === valdAvdelning)
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      <div className="content-header">
        <div>
          <h1>Medarbetare</h1>
          <div className="content-sub">{filtrerade.length} av {medarbetare.length} medarbetare</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="form-control"
            style={{ margin: 0, width: 'auto', fontSize: '.82rem' }}
            value={valdAvdelning}
            onChange={e => setValdAvdelning(e.target.value)}
          >
            <option value="Alla">Alla avdelningar</option>
            <option value="RN">RN</option>
            <option value="LN MMÖ">LN MMÖ</option>
            <option value="LN NKP">LN NKP</option>
            <option value="DISP">DISP</option>
          </select>
          <button className="btn btn-primary btn-sm" onClick={oppnaLaggTill}>+ Lägg till</button>
        </div>
      </div>

      <div className="content-body" style={{ overflowY: 'auto' }}>
        <div className="card">
          {filtrerade.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 0', borderBottom: '1px solid #f5f5f5' }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                background: AVDELNING_FARGER[m.avdelning] || '#F11C0A',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '.78rem', color: '#fff', flexShrink: 0
              }}>
                {m.fornamn[0]}{m.efternamn[0]}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.9rem' }}>
                  {m.fornamn} {m.efternamn}
                  {m.ar_admin && <span className="tag tag-indigo" style={{ fontSize: '.65rem', marginLeft: 6 }}>Admin</span>}
                </div>
                <div style={{ fontSize: '.75rem', color: '#aaa', marginTop: 2 }}>
                  KID: {m.kid} · Anst.nr: {m.anstallningsnummer || '–'} · {m.email} · {m.avdelning} · {m.schema_typ}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 7, alignItems: 'center' }}>
                <span className="tag" style={{ background: '#f5f5f5', color: '#666', fontSize: '.7rem' }}>{m.avdelning}</span>
                <button className="btn btn-secondary btn-sm" onClick={() => oppnaRedigera(m)}>✏ Redigera</button>
                <button
                  style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '.75rem', padding: '4px 6px', borderRadius: 4 }}
                  title="Avsluta anställning"
                  onMouseOver={e => e.target.style.color='#e53935'}
                  onMouseOut={e => e.target.style.color='#ccc'}
                  onClick={() => avslutaAnstallning(m)}
                >🚪</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL */}
      {visaModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <h3>{redigerar ? '✏️ Redigera medarbetare' : '👤 Lägg till medarbetare'}</h3>
              <button className="modal-close" onClick={() => setVisaModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div>
                  <label className="form-label">Förnamn</label>
                  <input className="form-control" value={form.fornamn} onChange={e => setForm(f => ({...f, fornamn: e.target.value}))} placeholder="Anna" />
                </div>
                <div>
                  <label className="form-label">Efternamn</label>
                  <input className="form-control" value={form.efternamn} onChange={e => setForm(f => ({...f, efternamn: e.target.value}))} placeholder="Karlsson" />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label className="form-label">KID</label>
                  <input className="form-control" value={form.kid} onChange={e => setForm(f => ({...f, kid: e.target.value}))} placeholder="1042" />
                </div>
                <div>
                  <label className="form-label">Anställningsnummer</label>
                  <input className="form-control" value={form.anstallningsnummer} onChange={e => setForm(f => ({...f, anstallningsnummer: e.target.value}))} placeholder="12345" />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label className="form-label">Avdelning</label>
                  <select className="form-control" value={form.avdelning} onChange={e => setForm(f => ({...f, avdelning: e.target.value}))}>
                    {AVDELNINGAR.map(a => <option key={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <label className="form-label">E-postadress</label>
              <input className="form-control" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="anna.k@eon.se" disabled={!!redigerar} />
              <label className="form-label">Schematyp</label>
              <select className="form-control" value={form.schema_typ} onChange={e => setForm(f => ({...f, schema_typ: e.target.value}))}>
                <option value="34.5h">34,5 h/v – skiftarbete</option>
                <option value="40h">40 h/v – dagtid (D-pass)</option>
              </select>
              <div className="checkbox-row">
                <input type="checkbox" checked={form.ar_admin} onChange={e => setForm(f => ({...f, ar_admin: e.target.checked}))} id="adm" />
                <label htmlFor="adm" style={{ fontWeight: 500, fontSize: '.86rem', cursor: 'pointer' }}>Admin-behörighet</label>
              </div>
              {!redigerar && (
                <>
                  <label className="form-label">Lösenord</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 13 }}>
                    <input className="form-control" style={{ marginBottom: 0, fontFamily: 'monospace', letterSpacing: '.5px' }} value={genereratLosenord} readOnly placeholder="Klicka Slumpa →" />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={slumpaLosenord} style={{ flexShrink: 0 }}>🎲 Slumpa</button>
                    {genereratLosenord && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={kopieraLosenord} style={{ flexShrink: 0 }}>
                        {kopierat ? '✅' : '📋'}
                      </button>
                    )}
                  </div>
                  <div className="info-box info-box-yellow">
                    ⚠️ När du trycker "Lägg till & skicka mail" öppnas ett färdigskrivet mail i din Outlook, med KID och lösenordet ovan ifyllt. <strong>Du måste själv trycka Skicka</strong> i Outlook – det skickas inte automatiskt av systemet.
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setVisaModal(false)}>Avbryt</button>
              <button className="btn btn-primary" onClick={handleSpara} disabled={sparar}>
                {sparar ? 'Sparar...' : redigerar ? 'Spara ändringar' : 'Lägg till & skicka mail'}
              </button>
            </div>
          </div>
        </div>
      )}
      {avslutaDialog && (
        <BekraftaModal
          titel="Avsluta anställning"
          meddelande={`Avsluta anställning för ${avslutaDialog.fornamn} ${avslutaDialog.efternamn}?

Detta kommer:
• Inaktivera kontot (kan inte logga in)
• Anonymisera namn och email
• Behålla schemahistorik anonymt

Åtgärden kan inte ångras.`}
          farligKnapp
          onBekrafta={bekraftaAvsluta}
          onAvbryt={() => setAvslutaDialog(null)}
        />
      )}
    </div>
  )
}
